"use client";

import { useMutation, useQuery } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FaExpand } from "react-icons/fa";
import { FiCamera, FiCheckSquare, FiDownload, FiFilter, FiShare2, FiUpload } from "react-icons/fi";
import { HiOutlinePhotograph } from "react-icons/hi";
import { LuLoader2 } from "react-icons/lu";
import { RiArrowDownSLine } from "react-icons/ri";
import { usePathname, useSearchParams } from "next/navigation";
import { AttachmentGalleryDialog, AttachmentItemActionButtons, AttachmentItemMedia, type ResolvedAttachment, resolveAttachment, useAttachmentItemController } from "@/app/[tenant]/(console)/scribe/playground/[id]/attachment-item";
import CameraCaptureModal from "@/components/camera-capture-modal";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import { useStudioViewer } from "@/components/photo-studio/use-studio-viewer";
import { useProgressNavigation } from "@/components/progress-bar";
import AttachmentAnnotationModal from "@/components/scribe/attachment-annotation-modal";
import BulkRenameAttachmentsModal from "@/components/scribe/bulk-rename-attachments-modal";
import BulkTagAttachmentsModal from "@/components/scribe/bulk-tag-attachments-modal";
import ChangePatientModal from "@/components/scribe/change-patient-modal";
import { GalleryAppointmentButton } from "@/components/scribe/gallery-appointment-button";
import GalleryPickerModal from "@/components/scribe/gallery-picker-modal";
import ImageCompareModal from "@/components/scribe/image-compare-modal";
import { ImageTagBadge } from "@/components/scribe/image-tag-badge";
import NotePickerModal from "@/components/scribe/note-picker-modal";
import ShareToPortalModal from "@/components/scribe/share-to-portal-modal";
import SkinAnalysisDialog from "@/components/scribe/skin-analysis-dialog";
import { Button, Card, Checkbox, Input } from "@/components/ui";
import { BulkActionsBar } from "@/components/ui/bulk-actions-bar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownSettingsButton } from "@/components/ui/dropdown";
import { MultiSelect, MultiSelectContent, MultiSelectItem } from "@/components/ui/multi-select";
import { PopoverTrigger } from "@/components/ui/popover";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import { useBeforeUnload } from "@/hooks/use-before-unload";
import { useFileDragDrop } from "@/hooks/use-file-drag-drop";
import { useModule } from "@/hooks/use-module";
import { formatDate, formatDateTime, formatName, toUtcDateKey } from "@/lib/format-data";
import { GALLERY_IMAGE_COMPRESSION_OPTIONS, calculateCompressionSavings, compressImages, formatFileSize, isImageUrl, isVideoFile, isVideoUrl } from "@/lib/image-compression";
import { NO_PHOTO_PERMISSION_REASON, PERMISSIONS, canAddPhotos, galleryPhotoActions, hasPermission } from "@/lib/permissions";
import { applyStudioToNewPhoto, isStudioImagePath } from "@/lib/photo-studio/studio-photo";
import { PHOTO_DELETE_PERMISSION_DENIED_TOAST, PHOTO_PERMISSION_DENIED_TOAST, isPermissionDeniedError } from "@/lib/photo-upload-errors";
import { cx, focusInput } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import type { DetachedChartDoses } from "@/types/medication";
import { NoteAttachment, NoteAttachmentNote, NoteAttachmentTag } from "@/types/notes";
import { PatientSummary } from "@/types/patient";
import { PatientSidebarCounts } from "@/types/patient-sidebar";
import { downloadFromSignedUrl, getSignedDownloadUrl } from "@/api/auth/gcs";
import { useStudioThumbnails } from "@/api/provider/photo-studio/studio-query";
import { detachChartDosesInPatientCharts, updateAttachmentsInInfiniteGalleryCache, useAnnotationMutation, useChangeAttachmentPatientMutation, useDuplicateAttachmentMutation, useRestoreAnnotationMutation, useSignedUrlUploadMutation, useTagMutations } from "@/api/provider/scribe/note-mutation";
import { noteQueryKeys, noteQueryOptions } from "@/api/provider/scribe/note-query";
import { deleteNoteAttachment, unlinkAttachmentFromNote, updateNoteAttachmentName } from "@/api/provider/scribe/notes";
import { useSetProfilePictureFromImageMutation } from "@/api/provider/user/patient/patient-mutation";
import { invalidateSidebarCounts, optimisticAdjustSidebarCounts, revertSidebarCounts } from "@/api/provider/user/patient/sidebar-cache-utils";
interface AttachmentData {
  id: string;
  attachmentId?: string;
  url?: string;
  signedUrl?: string;
  thumbnailSignedUrl?: string | null;
  gcsPath?: string;
  annotations?: any[];
  name?: string;
  date?: string;
  // Raw upload timestamp (with time) surfaced as a per-photo time label.
  uploadedAt?: string;
  isPlaceholder?: boolean;
  noteId?: string | null;
  patientId?: string;
  patient?: PatientSummary;
  note?: NoteAttachmentNote;
  tags?: NoteAttachmentTag[];
  visibleToPatient?: boolean;
  patientCaption?: string | null;
}
interface AttachmentsGridProps {
  // Data
  attachments: AttachmentData[];
  isLoading: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  loadMoreRef?: (node: HTMLDivElement | null) => void;
  patientId?: string;
  noteId?: string | null;
  /** Appointment of the note this grid belongs to. Without it, each photo
   *  uses its own note's appointment. */
  appointmentId?: string | null;

  // Display options
  size?: "sm" | "lg";
  groupByDate?: boolean;
  showUpload?: boolean;
  uploadDisabled?: boolean;
  // Additional actions rendered in the ⋮ menu, after the built-in add options
  dropdownActions?: React.ReactNode;
  // Extra buttons in the toolbar, just before Capture
  toolbarActions?: React.ReactNode;

  // Template attachment placeholders (only needed for note editor with templates)
  placeholderControls?: {
    isPlaceholder: (attachment: NoteAttachment) => boolean;
    convertingPlaceholderId: string | null;
    setConvertingPlaceholderId: (id: string | null) => void;
    convertPlaceholderToRealAttachment: (attachment: NoteAttachment) => Promise<NoteAttachment | null>;
    onPlaceholderDelete?: (attachmentId: string) => void;
  };

  // Callbacks
  onImageReplaced?: () => void;
  onTagIdsChange?: (tagIds: string[]) => void;
  tagIds?: string[];
  className?: string;
  showPatientLink?: boolean;
  showNoteLink?: boolean;
  clickToAnnotate?: boolean;
  // Enables the "Share to patient portal" action. Only set this from grids that
  // map `visibleToPatient`/`patientCaption` onto their attachments (the gallery
  // views), so the shared badge and "Stop sharing" state are accurate.
  enablePortalSharing?: boolean;
}

// Props for the memoized AttachmentGridItem component
interface AttachmentGridItemProps {
  attachment: AttachmentData;
  showCheckbox: boolean;
  noteId?: string | null;
  patientId?: string;
  allTags: NoteAttachmentTag[];
  isSelectionMode: boolean;
  isUploading: boolean;
  isAnyMutating: boolean;
  attachmentsCount: number;
  selectedAttachmentIds: Set<string>;
  editingNameId: string | null;
  editingName: string;
  savingNameId: string | null;
  showPatientLink: boolean;
  showNoteLink: boolean;
  placeholderControls?: {
    isPlaceholder: (attachment: NoteAttachment) => boolean;
    convertingPlaceholderId: string | null;
    setConvertingPlaceholderId: (id: string | null) => void;
    convertPlaceholderToRealAttachment: (attachment: NoteAttachment) => Promise<NoteAttachment | null>;
    onPlaceholderDelete?: (attachmentId: string) => void;
  };
  onToggleSelection: (attachmentId: string) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  onUnlinkFromNote?: (attachmentId: string) => void;
  onCompare: (attachment: AttachmentData) => void;
  onDuplicate: (attachmentId: string) => void;
  isDuplicating: boolean;
  onAssignTags: (attachmentId: string, tagIds: string[]) => void;
  onEditName: (attachment: NoteAttachment) => void;
  onSaveName: (attachmentId: string) => Promise<void>;
  onCancelEditName: () => void;
  setEditingName: (name: string) => void;
  setAnnotatingAttachment: (attachment: NoteAttachment) => void;
  setAnnotationModalOpen: (open: boolean) => void;
  openFullScreenAnnotationPage: (attachment: NoteAttachment) => void;
  /** Omitted when the user can't add photos, so placeholders don't open a picker. */
  handleFileChange?: (files: FileList | null, targetAttachment?: NoteAttachment, customName?: string) => Promise<void>;
  onCameraOpen?: (targetAttachment: NoteAttachment) => void;
  onOpenGallery?: (attachmentIndex: number) => void;
  attachmentIndex: number;
  onLinkToNote: (attachmentId: string) => void;
  onChangePatient: (attachmentId: string) => void;
  onSkinAnalysis?: (attachmentId: string) => void;
  onSetAsProfilePhoto?: (attachment: AttachmentData) => void;
  onShareToPortal?: (attachment: AttachmentData) => void;
}

// Convert attachment data to NoteAttachment format
const toNoteAttachment = (attachment: AttachmentData): NoteAttachment => {
  const isPlaceholder = attachment.isPlaceholder === true;
  return {
    id: attachment.id,
    attachmentId: isPlaceholder ? undefined : attachment.attachmentId || attachment.id,
    url: attachment.signedUrl || attachment.url || "",
    gcsPath: attachment.gcsPath || "",
    annotations: attachment.annotations || [],
    name: attachment.name || "",
    isPlaceholder
  };
};

// Memoized attachment grid item to prevent unnecessary re-renders
const AttachmentGridItem = memo(function AttachmentGridItem({
  attachment,
  showCheckbox,
  noteId,
  patientId,
  allTags,
  isSelectionMode,
  isUploading,
  isAnyMutating,
  attachmentsCount,
  selectedAttachmentIds,
  editingNameId,
  editingName,
  savingNameId,
  showPatientLink: _showPatientLink,
  showNoteLink,
  placeholderControls,
  onToggleSelection,
  onRemoveAttachment,
  onUnlinkFromNote,
  onCompare,
  onDuplicate,
  isDuplicating,
  onAssignTags,
  onEditName,
  onSaveName,
  onCancelEditName,
  setEditingName,
  setAnnotatingAttachment,
  setAnnotationModalOpen,
  openFullScreenAnnotationPage,
  handleFileChange,
  onCameraOpen,
  onOpenGallery,
  attachmentIndex,
  onLinkToNote,
  onChangePatient,
  onSkinAnalysis,
  onSetAsProfilePhoto,
  onShareToPortal
}: AttachmentGridItemProps) {
  const noteAttachment = toNoteAttachment(attachment);
  const attachmentIsVideo = isVideoUrl(attachment.gcsPath || attachment.url || attachment.name || "");

  // "Set as profile photo" needs a patient to set it on — the attachment's own
  // patient (global gallery) or the patient whose gallery we're viewing.
  const profilePhotoPatientId = attachment.patient?.id ?? patientId;
  // Only offer it for real image attachments: skip videos, placeholders
  // (no image yet), and non-image files such as PDFs from note uploads.
  const canSetAsProfilePhoto = !attachmentIsVideo && !attachment.isPlaceholder && isImageUrl(attachment.gcsPath || attachment.url || attachment.name || "");

  // Skin analysis runs on a photo, so hide it for videos, placeholders, and
  // non-image files.
  const canRunSkinAnalysis = canSetAsProfilePhoto;

  // Convert to NoteAttachment format to include note/patient info for links
  // For placeholders without dates, use undefined to avoid incorrect date grouping/sorting
  const attachmentResponse: NoteAttachment = {
    id: attachment.id,
    noteId: attachment.noteId || noteId || "",
    gcsPath: attachment.gcsPath || "",
    originalFilename: attachment.name,
    signedUrl: attachment.signedUrl || attachment.url,
    // Use the real (compressed) thumbnail so grid tiles don't download the
    // full-resolution image. resolveAttachment falls back to signedUrl/url
    // when no thumbnail is available, so callers that don't supply one are
    // unaffected. Loading full-res images into every tile blows the memory
    // budget on older iPads and triggers a tab reload.
    //
    // Videos have no generated thumbnail (the job only processes images) and
    // the backend signs thumbnail paths without an existence check, so a
    // video's thumbnailSignedUrl points to a missing thumb_*.jpg. The video
    // tile renders from thumbnailUrl, so forwarding it would break playback —
    // fall back to the full signed URL for videos instead.
    thumbnailSignedUrl: attachmentIsVideo ? undefined : attachment.thumbnailSignedUrl,
    annotations: attachment.annotations || [],
    name: attachment.name,
    createdDate: attachment.date,
    // Preserve placeholder data for template attachments
    isPlaceholder: attachment.isPlaceholder,
    attachmentId: attachment.isPlaceholder ? undefined : attachment.attachmentId || attachment.id,
    // Use note from attachment data, or create from noteId prop
    note: attachment.note ? {
      id: attachment.note.id,
      createdDate: attachment.note.createdDate || attachment.date,
      visitDate: attachment.note.visitDate,
      eventId: attachment.note.eventId,
      eventTitle: attachment.note.eventTitle
    } : noteId ? {
      id: noteId,
      createdDate: attachment.date
    } : undefined,
    // Use patient from attachment data if available (full PatientSummary from API)
    // Otherwise, we'll rely on patientId prop passed to the footer overlay
    patient: attachment.patient,
    tags: attachment.tags
  };
  const resolveForAnnotating = useCallback((attachmentToAnnotate: NoteAttachment): NoteAttachment => ({
    ...attachmentToAnnotate,
    url: attachmentToAnnotate.signedUrl || attachmentToAnnotate.url || "",
    attachmentId: attachmentToAnnotate.attachmentId || attachmentToAnnotate.id
  }), []);
  const openResolvedFullScreenAnnotation = useCallback(async () => {
    const resolvedAttachment = resolveForAnnotating(attachmentResponse);
    openFullScreenAnnotationPage(resolvedAttachment);
  }, [attachmentResponse, openFullScreenAnnotationPage, resolveForAnnotating]);
  const controller = useAttachmentItemController({
    attachment: attachmentResponse,
    shouldShowThumbnail: true,
    noteId,
    patientId,
    tagControls: {
      allTags,
      onAssignTags
    },
    ui: {
      disabled: isSelectionMode,
      isUploading,
      isAnyAttachmentMutating: isAnyMutating
    },
    onRemove: isSelectionMode ? undefined : onRemoveAttachment,
    onCompare: isSelectionMode || attachmentsCount <= 1 || attachmentIsVideo ? undefined : () => onCompare(attachment),
    onDuplicate: isSelectionMode ? undefined : () => onDuplicate(attachment.id),
    onRename: isSelectionMode ? undefined : () => onEditName(toNoteAttachment(attachment)),
    isDuplicating,
    nameEditing: isSelectionMode ? undefined : {
      editingNameId,
      editingName,
      setEditingName,
      savingNameId,
      handleEditName: onEditName,
      handleSaveName: onSaveName,
      handleCancelEditName: onCancelEditName
    },
    annotationControls: isSelectionMode ? undefined : {
      setAnnotatingAttachment: att => {
        setAnnotatingAttachment(resolveForAnnotating(att));
        setAnnotationModalOpen(true);
      },
      setAnnotationModalOpen
    },
    placeholderControls: placeholderControls ? {
      isPlaceholder: placeholderControls.isPlaceholder(noteAttachment),
      convertingPlaceholderId: placeholderControls.convertingPlaceholderId,
      setConvertingPlaceholderId: placeholderControls.setConvertingPlaceholderId,
      convertPlaceholderToRealAttachment: placeholderControls.convertPlaceholderToRealAttachment
    } : undefined,
    uploadControls: {
      handleFileChange,
      onCameraOpen
    },
    onOpenGallery,
    attachmentIndex
  });
  return <div className="relative">
      {showCheckbox && isSelectionMode && <div className="absolute left-1.5 top-1.5 z-10 cursor-pointer" onClick={e => {
      e.stopPropagation();
      onToggleSelection(attachment.id);
    }}>
          <Checkbox checked={selectedAttachmentIds.has(attachment.id)} className="h-5 w-5 border-2 border-white bg-black/20 shadow-sm data-[state=checked]:border-primary data-[state=checked]:bg-primary" />
        </div>}

      <div onClickCapture={isSelectionMode ? (e_0: React.MouseEvent) => {
      // Only toggle selection on Command/Ctrl-click
      if (e_0.metaKey || e_0.ctrlKey) {
        e_0.preventDefault();
        e_0.stopPropagation();
        onToggleSelection(attachment.id);
      }
    } : undefined} className={cx(isSelectionMode && selectedAttachmentIds.has(attachment.id) && "rounded-lg ring-2 ring-primary")}>
        <div className="group relative">
          <div className={controller.containerClassName} onClick={() => void controller.onContainerClick()}>
            <AttachmentItemMedia resolved={controller.resolved} attachment={attachmentResponse} isUploading={controller.isUploading} converting={controller.converting} isOptimisticEntry={controller.isOptimisticEntry} hasValidImageUrl={controller.hasValidImageUrl} onCameraOpen={controller.onPlaceholderCameraOpen} />
          </div>

          {attachment.visibleToPatient && <div className="absolute -left-1 -top-1 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-caption-strong text-primary-foreground shadow-sm" title={attachment.patientCaption ? `Shared to patient portal: "${attachment.patientCaption}"` : "Shared to patient portal"}>
              <FiShare2 className="h-3 w-3" />
              Shared
            </div>}

          <AttachmentItemActionButtons show={!controller.disabled && !controller.isOptimisticEntry && !!controller.resolved.id && controller.canRemove} isAnyAttachmentMutating={controller.isAnyAttachmentMutating} isDuplicating={isDuplicating} onEditAnnotations={isSelectionMode || controller.resolved.isVideo ? undefined : () => {
          setAnnotatingAttachment(resolveForAnnotating(attachmentResponse));
          setAnnotationModalOpen(true);
        }} onEditAnnotationsFullScreen={isSelectionMode || controller.resolved.isVideo ? undefined : () => void openResolvedFullScreenAnnotation()} onDownload={isSelectionMode ? undefined : async () => {
          const gcsPath = attachment.gcsPath;
          if (!gcsPath) return;
          try {
            const filename = attachment.name || `photo-${attachment.id.slice(0, 8)}.jpg`;
            const result = await getSignedDownloadUrl(gcsPath, filename);
            if (!result?.signedUrl) return;
            await downloadFromSignedUrl(result.signedUrl, filename);
          } catch (error) {
            console.error("Failed to download attachment:", error);
          }
        }} onCompare={isSelectionMode || attachmentsCount <= 1 || attachmentIsVideo ? undefined : () => onCompare(attachment)} onRemove={isSelectionMode ? undefined : onRemoveAttachment} onUnlinkFromNote={isSelectionMode || !noteId || !onUnlinkFromNote ? undefined : () => onUnlinkFromNote(attachment.id)} onDuplicate={isSelectionMode ? undefined : () => onDuplicate(attachment.id)} onRename={isSelectionMode ? undefined : () => onEditName(toNoteAttachment(attachment))} onLinkToNote={isSelectionMode || !showNoteLink ? undefined : () => onLinkToNote(attachment.id)} onChangePatient={isSelectionMode ? undefined : () => onChangePatient(attachment.id)} onSkinAnalysis={isSelectionMode || !onSkinAnalysis || !canRunSkinAnalysis ? undefined : () => onSkinAnalysis(attachment.id)} onSetAsProfilePhoto={isSelectionMode || !canSetAsProfilePhoto || !onSetAsProfilePhoto || !profilePhotoPatientId ? undefined : () => onSetAsProfilePhoto(attachment)} onShareToPortal={isSelectionMode || !canSetAsProfilePhoto || !onShareToPortal ? undefined : () => onShareToPortal(attachment)} isSharedToPortal={attachment.visibleToPatient} attachmentId={controller.resolved.id} />

          {/* Name and tags below image */}
          {!controller.isOptimisticEntry && <div className="mt-1.5 space-y-1">
              {editingNameId === attachment.id && !isSelectionMode ? <div className="flex items-center gap-1">
                  <Input value={editingName} onChange={e_1 => setEditingName(e_1.target.value)} className="h-6 flex-1 !rounded px-1.5 py-0 text-caption" placeholder="Enter name..." onKeyDown={e_2 => {
              if (e_2.key === "Enter") {
                e_2.preventDefault();
                void onSaveName(attachment.id);
              } else if (e_2.key === "Escape") {
                e_2.preventDefault();
                onCancelEditName();
              }
            }} onBlur={() => void onSaveName(attachment.id)} autoFocus disabled={savingNameId === attachment.id} />
                  {savingNameId === attachment.id && <LuLoader2 className="h-3 w-3 animate-spin text-foreground-muted" />}
                </div> : <p className="truncate text-caption-strong text-foreground" title={controller.resolved.name || "Untitled"}>
                  {controller.resolved.name || "Untitled"}
                </p>}
              {attachment.uploadedAt && <p className="truncate text-caption text-foreground-muted" title={`Uploaded ${formatDateTime(attachment.uploadedAt)}`}>
                  {formatDateTime(attachment.uploadedAt, true, false)}
                </p>}
              <div className="flex flex-wrap items-center gap-1" onClick={e_3 => e_3.stopPropagation()}>
                {controller.resolved.tags.map(tag => <ImageTagBadge key={tag.id} tag={tag} onRemoveTag={!isSelectionMode ? controller.handleRemoveTag : undefined} />)}
                {!isSelectionMode && <ImageTagBadge isAddButton onAddTag={controller.handleAddTag} availableTags={allTags} assignedTagIds={controller.resolved.tags.map(t => t.id)} />}
              </div>
            </div>}
        </div>
      </div>
    </div>;
});
export function AttachmentsGrid({
  attachments: sourceAttachments,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  loadMoreRef,
  patientId,
  noteId,
  appointmentId,
  size = "lg",
  groupByDate = false,
  showUpload = true,
  uploadDisabled = false,
  dropdownActions,
  toolbarActions,
  placeholderControls,
  onImageReplaced,
  onTagIdsChange,
  tagIds: controlledTagIds,
  className,
  showPatientLink = false,
  showNoteLink = true,
  clickToAnnotate = false,
  enablePortalSharing = false
}: AttachmentsGridProps) {
  const {
    currentUser
  } = useUser();
  const canDownload = hasPermission(currentUser, PERMISSIONS.CLINICAL_GALLERY_DOWNLOAD);
  const photoActions = galleryPhotoActions(currentUser);
  const canViewNotes = hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ);

  // Studio Mode: thumbnails show each photo on its backdrop (one batch request
  // per 100 photos). Photos without a Studio version keep their own thumbnail.
  const studioThumbnailIds = useMemo(() => sourceAttachments.filter(a => !a.isPlaceholder && !a.id.startsWith("temp-") && isStudioImagePath(a.gcsPath)).map(a_0 => a_0.attachmentId || a_0.id), [sourceAttachments]);
  const studioThumbnails = useStudioThumbnails(studioThumbnailIds, studioThumbnailIds.length > 0);
  const attachments = useMemo(() => studioThumbnails.size === 0 ? sourceAttachments : sourceAttachments.map(a_1 => {
    const studioUrl = studioThumbnails.get(a_1.attachmentId || a_1.id);
    return studioUrl ? {
      ...a_1,
      thumbnailSignedUrl: studioUrl
    } : a_1;
  }), [sourceAttachments, studioThumbnails]);
  // The gallery only requires read access, but setting a profile photo hits an
  // endpoint that requires profile-write — gate the action so read-only roles
  // don't see an option that would 403.
  const canEditProfilePicture = hasPermission(currentUser, PERMISSIONS.PATIENTS_PROFILES_WRITE);
  // Adding photos needs its own permission (see canAddPhotos); without it the
  // upload routes return 403, so say why instead of letting every shot fail.
  const addBlockedReason = uploadDisabled ? "Search for a patient first" : !canAddPhotos(currentUser, noteId) ? NO_PHOTO_PERMISSION_REASON : undefined;
  const addDisabled = addBlockedReason !== undefined;
  const sizeClasses = {
    sm: "grid-cols-2",
    lg: "grid-cols-2 md:grid-cols-4"
  };
  const {
    data: allTags = []
  } = useQuery(noteQueryOptions.tags());
  const {
    assignTagsMutation
  } = useTagMutations();

  // Use controlled tagIds if provided, otherwise manage internally
  const [internalTagIds, setInternalTagIds] = useState<string[]>([]);
  const selectedTagIds = controlledTagIds ?? internalTagIds;
  const selectedTagNames = allTags.filter(tag => selectedTagIds.includes(tag.id)).map(tag_0 => tag_0.name).join(", ");

  // State
  const [annotationModalOpen, setAnnotationModalOpen] = useState(false);
  const [annotatingAttachment, setAnnotatingAttachment] = useState<NoteAttachment | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<AttachmentData | null>(null);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [compareSourceImage, setCompareSourceImage] = useState<NoteAttachment | null>(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<string>>(new Set());
  const [bulkRenameModalOpen, setBulkRenameModalOpen] = useState(false);
  const [shareToPortalAttachment, setShareToPortalAttachment] = useState<AttachmentData | null>(null);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkTagModalOpen, setBulkTagModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [placeholderCameraTarget, setPlaceholderCameraTarget] = useState<NoteAttachment | null>(null);
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);
  const hasSkinAnalysis = useModule("SKIN_ANALYSIS");
  const [skinAnalysisAttachmentId, setSkinAnalysisAttachmentId] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [savingNameId, setSavingNameId] = useState<string | null>(null);
  const [preparingPlaceholderIds, setPreparingPlaceholderIds] = useState<Set<string>>(new Set());
  const setPlaceholderPreparing = useCallback((attachmentId: string | undefined, preparing: boolean) => {
    if (!attachmentId) return;
    setPreparingPlaceholderIds(prev => {
      const next = new Set(prev);
      if (preparing) {
        next.add(attachmentId);
      } else {
        next.delete(attachmentId);
      }
      return next;
    });
  }, []);

  // Handle tag filter changes
  const handleTagIdsChange = useCallback((newTagIds: string[]) => {
    if (controlledTagIds === undefined) {
      // Only update internal state if not controlled
      setInternalTagIds(newTagIds);
    }
    onTagIdsChange?.(newTagIds);
  }, [onTagIdsChange, controlledTagIds]);
  const hasAny = attachments.length > 0;
  const canShowSelectionMode = attachments.length > 1;

  // Compression state
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);

  // Upload progress state for signed URL uploads
  const [uploadProgress, setUploadProgress] = useState<Map<number, {
    loaded: number;
    total: number;
  }>>(new Map());

  // Mutations
  const signedUrlUploadMutation = useSignedUrlUploadMutation(patientId || "", noteId || null);
  const isUploading = signedUrlUploadMutation.isPending;
  // Only compression blocks the UI — uploads run in the background
  const isProcessing = isCompressing;
  useBeforeUnload(isCompressing || isUploading, isCompressing ? "Images are being compressed. Are you sure you want to leave? Your progress will be lost." : "Files are being uploaded. Are you sure you want to leave? Your uploads may be cancelled.");
  const updateAnnotationsMutation = useAnnotationMutation(patientId || "", noteId || null);
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const tenantKey = pathname.split("/")[1] || "";
  const openFullScreenAnnotationPage = useCallback((attachment: NoteAttachment) => {
    const attachmentId_0 = attachment.attachmentId || attachment.id;
    if (!attachmentId_0) return;
    const search = searchParams.toString();
    const returnTo = `${pathname}${search ? `?${search}` : ""}`;
    const target = `/attachments/${attachmentId_0}/annotate?returnTo=${encodeURIComponent(returnTo)}`;
    navigateWithProgress(target);
  }, [navigateWithProgress, pathname, searchParams]);
  const openAnnotatingAttachmentFullScreen = useCallback(async () => {
    if (!annotatingAttachment) return;
    openFullScreenAnnotationPage(annotatingAttachment);
  }, [annotatingAttachment, openFullScreenAnnotationPage]);
  const annotationDraftScope = useMemo(() => {
    const attachmentId_1 = annotatingAttachment?.attachmentId || annotatingAttachment?.id;
    if (!tenantKey || !currentUser?.id || !attachmentId_1) return null;
    return {
      tenantKey,
      userId: currentUser.id,
      attachmentId: attachmentId_1
    };
  }, [annotatingAttachment, currentUser?.id, tenantKey]);
  const restoreAnnotationMutation = useRestoreAnnotationMutation(patientId || "", noteId || null);

  // Delete attachment mutation
  const deleteAttachmentMutation = useMutation<DetachedChartDoses | null, Error, string, {
    previousSidebarCounts?: PatientSidebarCounts;
    attachmentId?: string;
    previousInfinite?: Array<[readonly unknown[], unknown]>;
    previousNoteAttachments?: NoteAttachment[];
    mutationNoteId?: string | null;
    mutationPatientId?: string;
  }>({
    mutationFn: async (attachmentId_2: string) => await deleteNoteAttachment(attachmentId_2),
    onMutate: async attachmentId_3 => {
      if (!patientId) return {};

      // Capture ids at mutate time so rollback targets the correct caches
      // even if the selected note/patient changes mid-request.
      const mutationNoteId = noteId ?? null;
      const mutationPatientId = patientId;

      // Cancel any outgoing refetches — use broad key to match all variants
      await queryClient.cancelQueries({
        queryKey: [...noteQueryKeys.all, "attachments-infinite"],
        exact: false
      });
      if (mutationNoteId) {
        await queryClient.cancelQueries({
          queryKey: noteQueryKeys.attachments({
            noteId: mutationNoteId
          })
        });
      }

      // Snapshot for rollback
      const previousInfinite = queryClient.getQueriesData({
        queryKey: [...noteQueryKeys.all, "attachments-infinite"],
        exact: false
      });
      const previousNoteAttachments = mutationNoteId ? queryClient.getQueryData<NoteAttachment[]>(noteQueryKeys.attachments({
        noteId: mutationNoteId
      })) : undefined;

      // Optimistically remove the attachment from the cache
      queryClient.setQueriesData({
        queryKey: [...noteQueryKeys.all, "attachments-infinite"],
        exact: false
      }, (oldData: any) => {
        if (!oldData?.pages) return oldData;
        const pages = oldData.pages.map((page: any) => ({
          ...page,
          data: page.data.filter((attachment_0: any) => attachment_0.id !== attachmentId_3)
        }));
        return {
          ...oldData,
          pages
        };
      });
      if (mutationNoteId) {
        queryClient.setQueryData(noteQueryKeys.attachments({
          noteId: mutationNoteId
        }), (oldData_0: any) => {
          if (!oldData_0) return oldData_0;
          return oldData_0.filter((attachment_1: any) => attachment_1.id !== attachmentId_3);
        });
      }
      const previousSidebarCounts = optimisticAdjustSidebarCounts(mutationPatientId, {
        attachmentsCount: -1
      });
      return {
        previousSidebarCounts,
        attachmentId: attachmentId_3,
        previousInfinite,
        previousNoteAttachments,
        mutationNoteId,
        mutationPatientId
      };
    },
    onSuccess: async detached => {
      detachChartDosesInPatientCharts(detached);
      toast({
        title: "Success",
        description: "Attachment deleted successfully",
        variant: "success"
      });
    },
    onError: (err, _vars, ctx) => {
      ctx?.previousInfinite?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      if (ctx?.mutationNoteId && ctx?.previousNoteAttachments !== undefined) {
        queryClient.setQueryData(noteQueryKeys.attachments({
          noteId: ctx.mutationNoteId
        }), ctx.previousNoteAttachments);
      }
      if (ctx?.mutationPatientId) {
        revertSidebarCounts(ctx.mutationPatientId, ctx?.previousSidebarCounts);
      }
      toast(isPermissionDeniedError(err) ? PHOTO_DELETE_PERMISSION_DENIED_TOAST : {
        title: "Error",
        description: "Failed to delete attachment",
        variant: "error"
      });
    }
  });

  // Unlink attachment from note (remove from note but keep in gallery)
  const unlinkMutation = useMutation<boolean | null, Error, {
    attachmentId: string;
    targetNoteId: string;
  }, {
    previousNoteAttachments?: NoteAttachment[];
    previousInfinite?: Array<[readonly unknown[], unknown]>;
  }>({
    mutationFn: async ({
      attachmentId: attachmentId_4,
      targetNoteId
    }) => {
      return await unlinkAttachmentFromNote(attachmentId_4, targetNoteId);
    },
    onMutate: async ({
      attachmentId: attachmentId_5,
      targetNoteId: targetNoteId_0
    }) => {
      await queryClient.cancelQueries({
        queryKey: noteQueryKeys.attachments({
          noteId: targetNoteId_0
        })
      });
      if (patientId) {
        await queryClient.cancelQueries({
          queryKey: noteQueryKeys.attachmentsInfinite({
            patientId
          }),
          exact: false
        });
      }

      // Snapshot for rollback
      const previousNoteAttachments_0 = queryClient.getQueryData<NoteAttachment[]>(noteQueryKeys.attachments({
        noteId: targetNoteId_0
      }));
      const previousInfinite_0 = patientId ? queryClient.getQueriesData({
        queryKey: noteQueryKeys.attachmentsInfinite({
          patientId
        }),
        exact: false
      }) : undefined;

      // Optimistically remove from note attachments cache
      queryClient.setQueryData(noteQueryKeys.attachments({
        noteId: targetNoteId_0
      }), (oldData_1: NoteAttachment[] | undefined) => {
        if (!oldData_1) return oldData_1;
        return oldData_1.filter(att => att.id !== attachmentId_5);
      });

      // Optimistically clear the note association in the infinite gallery
      // cache so the gallery UI reflects the unlink without a refetch. Clear
      // both `note` and `noteId` and match on either, since entries may
      // carry the association on one field or the other.
      if (patientId) {
        updateAttachmentsInInfiniteGalleryCache(patientId, att_0 => ({
          ...att_0,
          note: undefined,
          noteId: null
        }), att_1 => att_1.id === attachmentId_5 && (att_1.note?.id === targetNoteId_0 || att_1.noteId === targetNoteId_0));
      }
      return {
        previousNoteAttachments: previousNoteAttachments_0,
        previousInfinite: previousInfinite_0
      };
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Attachment removed from note",
        variant: "success"
      });
    },
    onError: (_err, {
      targetNoteId: targetNoteId_1
    }, ctx_0) => {
      if (ctx_0?.previousNoteAttachments !== undefined) {
        queryClient.setQueryData(noteQueryKeys.attachments({
          noteId: targetNoteId_1
        }), ctx_0.previousNoteAttachments);
      }
      ctx_0?.previousInfinite?.forEach(([key_0, data_0]) => {
        queryClient.setQueryData(key_0, data_0);
      });
      toast({
        title: "Error",
        description: "Failed to remove attachment from note",
        variant: "error"
      });
    }
  });

  // Update name mutation with optimistic updates to prevent flickering
  const updateNameMutation = useMutation<boolean | null, Error, {
    attachmentId: string;
    name: string;
  }, {
    previousNoteAttachments?: NoteAttachment[];
    previousInfinite?: Array<[readonly unknown[], unknown]>;
    mutationNoteId?: string | null;
    mutationPatientId?: string;
  }>({
    mutationFn: async ({
      attachmentId: attachmentId_6,
      name
    }: {
      attachmentId: string;
      name: string;
    }) => await updateNoteAttachmentName(attachmentId_6, name),
    onMutate: async ({
      attachmentId: attachmentId_7,
      name: name_0
    }) => {
      // Capture ids at mutate time so rollback targets the correct caches
      // even if the selected note/patient changes mid-request.
      const mutationNoteId_0 = noteId ?? null;
      const mutationPatientId_0 = patientId;

      // Cancel any outgoing refetches to prevent overwriting our optimistic update
      if (mutationNoteId_0) {
        await queryClient.cancelQueries({
          queryKey: noteQueryKeys.attachments({
            noteId: mutationNoteId_0
          })
        });
      }
      if (mutationPatientId_0) {
        await queryClient.cancelQueries({
          queryKey: noteQueryKeys.attachmentsInfinite({
            patientId: mutationPatientId_0
          }),
          exact: false
        });
      }

      // Snapshot the previous values for rollback
      const previousNoteAttachments_1 = mutationNoteId_0 ? queryClient.getQueryData<NoteAttachment[]>(noteQueryKeys.attachments({
        noteId: mutationNoteId_0
      })) : undefined;
      const previousInfinite_1 = mutationPatientId_0 ? queryClient.getQueriesData({
        queryKey: noteQueryKeys.attachmentsInfinite({
          patientId: mutationPatientId_0
        }),
        exact: false
      }) : undefined;

      // Optimistically update the attachment name in note attachments cache
      const matchesAttachment = (att_2: any) => att_2.id === attachmentId_7 || att_2.attachmentId === attachmentId_7;
      if (mutationNoteId_0) {
        queryClient.setQueryData(noteQueryKeys.attachments({
          noteId: mutationNoteId_0
        }), (oldData_2: NoteAttachment[] | undefined) => {
          if (!oldData_2) return oldData_2;
          return oldData_2.map(att_3 => matchesAttachment(att_3) ? {
            ...att_3,
            name: name_0
          } : att_3);
        });
      }

      // Optimistically update the attachment name in infinite attachments cache
      if (mutationPatientId_0) {
        queryClient.setQueriesData({
          queryKey: noteQueryKeys.attachmentsInfinite({
            patientId: mutationPatientId_0
          }),
          exact: false
        }, (oldData_3: any) => {
          if (!oldData_3?.pages) return oldData_3;
          return {
            ...oldData_3,
            pages: oldData_3.pages.map((page_0: any) => ({
              ...page_0,
              data: page_0.data.map((att_4: any) => matchesAttachment(att_4) ? {
                ...att_4,
                name: name_0
              } : att_4)
            }))
          };
        });
      }
      return {
        previousNoteAttachments: previousNoteAttachments_1,
        previousInfinite: previousInfinite_1,
        mutationNoteId: mutationNoteId_0,
        mutationPatientId: mutationPatientId_0
      };
    },
    onError: (_err_0, _vars_0, context) => {
      if (context?.mutationNoteId && context?.previousNoteAttachments !== undefined) {
        queryClient.setQueryData(noteQueryKeys.attachments({
          noteId: context.mutationNoteId
        }), context.previousNoteAttachments);
      }
      context?.previousInfinite?.forEach(([key_1, data_1]) => {
        queryClient.setQueryData(key_1, data_1);
      });
      toast({
        title: "Update failed",
        description: "Failed to update attachment name. Please try again.",
        variant: "error"
      });
    }
  });

  // Bulk delete mutation with optimistic updates
  const bulkDeleteMutation = useMutation<number, Error, string[], {
    previousNoteAttachments?: NoteAttachment[];
    previousInfinite?: Array<[readonly unknown[], unknown]>;
    previousSidebarCounts?: PatientSidebarCounts;
    mutationNoteId?: string | null;
    mutationPatientId?: string;
  }>({
    mutationFn: async (attachmentIds: string[]) => {
      await Promise.all(attachmentIds.map(async id => detachChartDosesInPatientCharts(await deleteNoteAttachment(id))));
      return attachmentIds.length;
    },
    onMutate: async attachmentIds_0 => {
      const attachmentIdSet = new Set(attachmentIds_0);

      // Capture ids at mutate time so rollback targets the correct caches
      // even if the selected note/patient changes mid-request.
      const mutationNoteId_1 = noteId ?? null;
      const mutationPatientId_1 = patientId;

      // Cancel any outgoing refetches
      if (mutationPatientId_1) {
        await queryClient.cancelQueries({
          queryKey: noteQueryKeys.attachmentsInfinite({
            patientId: mutationPatientId_1
          }),
          exact: false
        });
      }
      if (mutationNoteId_1) {
        await queryClient.cancelQueries({
          queryKey: noteQueryKeys.attachments({
            noteId: mutationNoteId_1
          })
        });
      }

      // Snapshot previous values for rollback
      const previousNoteAttachments_2 = mutationNoteId_1 ? queryClient.getQueryData<NoteAttachment[]>(noteQueryKeys.attachments({
        noteId: mutationNoteId_1
      })) : undefined;
      const previousInfinite_2 = mutationPatientId_1 ? queryClient.getQueriesData({
        queryKey: noteQueryKeys.attachmentsInfinite({
          patientId: mutationPatientId_1
        }),
        exact: false
      }) : undefined;

      // Optimistically remove from note attachments cache
      if (mutationNoteId_1) {
        queryClient.setQueryData(noteQueryKeys.attachments({
          noteId: mutationNoteId_1
        }), (oldData_4: NoteAttachment[] | undefined) => {
          if (!oldData_4) return oldData_4;
          return oldData_4.filter(att_5 => !attachmentIdSet.has(att_5.id));
        });
      }

      // Optimistically remove from infinite attachments cache
      if (mutationPatientId_1) {
        queryClient.setQueriesData({
          queryKey: noteQueryKeys.attachmentsInfinite({
            patientId: mutationPatientId_1
          }),
          exact: false
        }, (oldData_5: any) => {
          if (!oldData_5?.pages) return oldData_5;
          return {
            ...oldData_5,
            pages: oldData_5.pages.map((page_1: any) => ({
              ...page_1,
              data: page_1.data.filter((att_6: any) => !attachmentIdSet.has(att_6.id))
            }))
          };
        });
      }

      // Update sidebar counts optimistically
      const previousSidebarCounts_0 = mutationPatientId_1 ? optimisticAdjustSidebarCounts(mutationPatientId_1, {
        attachmentsCount: -attachmentIds_0.length
      }) : undefined;
      return {
        previousNoteAttachments: previousNoteAttachments_2,
        previousInfinite: previousInfinite_2,
        previousSidebarCounts: previousSidebarCounts_0,
        mutationNoteId: mutationNoteId_1,
        mutationPatientId: mutationPatientId_1
      };
    },
    onError: (err_0, _attachmentIds, context_0) => {
      if (context_0?.mutationNoteId && context_0?.previousNoteAttachments !== undefined) {
        queryClient.setQueryData(noteQueryKeys.attachments({
          noteId: context_0.mutationNoteId
        }), context_0.previousNoteAttachments);
      }
      context_0?.previousInfinite?.forEach(([key_2, data_2]) => {
        queryClient.setQueryData(key_2, data_2);
      });
      if (context_0?.mutationPatientId && context_0?.previousSidebarCounts) {
        revertSidebarCounts(context_0.mutationPatientId, context_0.previousSidebarCounts);
      }

      // Bulk delete uses Promise.all, which rejects on the first failure even
      // when other deletes succeeded server-side. The snapshot rollback above
      // gives the user immediate visual revert, but it reintroduces any
      // already-deleted items as ghosts. Invalidate the affected caches so
      // the server reconciles the true state on the next refetch.
      if (context_0?.mutationPatientId) {
        queryClient.invalidateQueries({
          queryKey: noteQueryKeys.attachmentsInfinite({
            patientId: context_0.mutationPatientId
          }),
          exact: false
        });
        invalidateSidebarCounts(context_0.mutationPatientId);
      }
      if (context_0?.mutationNoteId) {
        queryClient.invalidateQueries({
          queryKey: noteQueryKeys.attachments({
            noteId: context_0.mutationNoteId
          })
        });
      }
      toast(isPermissionDeniedError(err_0) ? PHOTO_DELETE_PERMISSION_DENIED_TOAST : {
        title: "Error",
        description: "Failed to delete some photos. Please try again.",
        variant: "error"
      });
    }
  });

  // Duplicate attachment mutation
  const duplicateAttachmentMutation = useDuplicateAttachmentMutation(patientId || "", noteId || null);

  // Gallery lightbox state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Pre-compute attachment id -> index map for O(1) gallery index lookup
  const attachmentIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    attachments.forEach((att_7, i) => map.set(att_7.id, i));
    return map;
  }, [attachments]);

  // Note picker / Change patient modal state
  const [notePickerAttachment, setNotePickerAttachment] = useState<ResolvedAttachment | null>(null);
  const [changePatientAttachment, setChangePatientAttachment] = useState<ResolvedAttachment | null>(null);

  // Link attachment to note (inline mutation since noteId comes from picker)
  const linkToNoteMutation = useMutation({
    mutationFn: async ({
      noteId: targetNoteId_2,
      attachmentId: attachmentId_8
    }: {
      noteId: string;
      attachmentId: string;
    }) => {
      const {
        linkAttachmentsToNote
      } = await import("@/api/provider/scribe/notes");
      return await linkAttachmentsToNote(targetNoteId_2, [attachmentId_8]);
    },
    onSuccess: (data_3, {
      noteId: targetNoteId_3
    }) => {
      if (data_3 && data_3.length > 0) {
        queryClient.setQueryData<NoteAttachment[]>(noteQueryKeys.attachments({
          noteId: targetNoteId_3
        }), prev_0 => prev_0 ? [...data_3, ...prev_0] : data_3);

        // Also update the infinite gallery cache in place so the attachment
        // reflects its new `note` association without requiring a refetch.
        if (patientId) {
          const updatedById = new Map(data_3.map(att_8 => [att_8.id, att_8]));
          updateAttachmentsInInfiniteGalleryCache(patientId, att_9 => updatedById.get(att_9.id) ?? att_9, att_10 => updatedById.has(att_10.id));
        }
      }
      toast({
        title: "Success",
        description: "Attachment linked to note",
        variant: "success"
      });
      setNotePickerAttachment(null);
      setGalleryOpen(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to link attachment to note",
        variant: "error"
      });
    }
  });

  // Change patient mutation
  const changePatientMutation = useChangeAttachmentPatientMutation(patientId || "");

  // Set an attachment as a patient's profile photo (fetches the GCS image and
  // reuses the standard profile-picture upload endpoint).
  const setProfilePictureMutation = useSetProfilePictureFromImageMutation();
  const handleSetAsProfilePhoto = useCallback((attachment_2: AttachmentData) => {
    const targetPatientId = attachment_2.patient?.id ?? patientId;
    if (!targetPatientId) return;
    setProfilePictureMutation.mutate({
      patientId: targetPatientId,
      gcsPath: attachment_2.gcsPath,
      signedUrl: attachment_2.signedUrl || attachment_2.url,
      name: attachment_2.name
    });
  }, [patientId, setProfilePictureMutation]);
  const handleShareToPortal = useCallback((attachment_3: AttachmentData) => {
    setShareToPortalAttachment(attachment_3);
  }, []);
  const isPlaceholderConversionPending = placeholderControls?.convertingPlaceholderId != null;
  const isAnyMutating = deleteAttachmentMutation.isPending || unlinkMutation.isPending || updateNameMutation.isPending || updateAnnotationsMutation.isPending || isPlaceholderConversionPending || bulkDeleteMutation.isPending || assignTagsMutation.isPending || duplicateAttachmentMutation.isPending;

  // Group attachments by date if needed
  const groupedAttachments = useMemo(() => {
    if (!groupByDate) return null;
    const groups = new Map<string, AttachmentData[]>();
    // Only group attachments that have dates
    attachments.forEach(attachment_4 => {
      if (attachment_4.date) {
        // `date` is the page's calendar day, a `YYYY-MM-DD` string from the
        // API or from an optimistic upload page.
        const dateKey = toUtcDateKey(attachment_4.date);
        if (!groups.has(dateKey)) {
          groups.set(dateKey, []);
        }
        groups.get(dateKey)!.push(attachment_4);
      }
    });
    return Array.from(groups.entries()).map(([dateKey_0, items]) => {
      const appointments = new Map<string | null, AttachmentData[]>();
      for (const item of items) {
        const eventId = item.note?.eventId ?? null;
        const appointmentItems = appointments.get(eventId) ?? [];
        appointmentItems.push(item);
        appointments.set(eventId, appointmentItems);
      }
      return {
        dateKey: dateKey_0,
        appointments: Array.from(appointments, ([eventId_0, data_4]) => ({
          eventId: eventId_0,
          title: data_4.find(item_0 => item_0.note?.eventTitle)?.note?.eventTitle,
          data: data_4
        })).sort((a_2, b) => Number(!a_2.eventId) - Number(!b.eventId))
      };
    }).sort((a_3, b_0) => b_0.dateKey.localeCompare(a_3.dateKey));
  }, [attachments, groupByDate]);

  // File handling
  const handleFileChange = useCallback(async (files: FileList | null, targetAttachment?: NoteAttachment, customName?: string, studioMask?: Blob) => {
    if (!files || files.length === 0 || !patientId) return;
    const fileArray = Array.from(files);
    const MAX_ATTACHMENTS = 50;
    const preparingPlaceholderId = targetAttachment?.isPlaceholder ? targetAttachment.id : undefined;
    if (fileArray.length > MAX_ATTACHMENTS) {
      toast({
        title: "Too many files",
        description: `Maximum ${MAX_ATTACHMENTS} attachments allowed per upload. Please select fewer files.`,
        variant: "error"
      });
      return;
    }
    setPlaceholderPreparing(preparingPlaceholderId, true);
    try {
      // Separate videos (no compression) from images (compress)
      const imageFiles = fileArray.filter(f => !isVideoFile(f));
      const videoFiles = fileArray.filter(f_0 => isVideoFile(f_0));

      // Only show compression UI if there are images to compress
      if (imageFiles.length > 0) {
        setIsCompressing(true);
        setCompressionProgress({
          completed: videoFiles.length,
          total: fileArray.length
        });
      }
      const compressionResults = await compressImages(imageFiles, GALLERY_IMAGE_COMPRESSION_OPTIONS, completed => setCompressionProgress({
        completed: videoFiles.length + completed,
        total: fileArray.length
      }));

      // Add video files as-is (no compression)
      for (const videoFile of videoFiles) {
        compressionResults.push({
          file: videoFile,
          originalName: videoFile.name,
          wasCompressed: false,
          originalSize: videoFile.size,
          compressedSize: videoFile.size
        });
      }
      setIsCompressing(false);
      setCompressionProgress(null);

      // Log compression savings for debugging
      const savings = calculateCompressionSavings(compressionResults);
      if (savings.savedBytes > 0) {
        console.log(`Image compression saved ${formatFileSize(savings.savedBytes)} (${savings.savedPercentage.toFixed(1)}%)`);
      }
      if (compressionResults.length > 0) {
        const compressedFiles = compressionResults.map(r => r.file);
        const names = compressionResults.map((_r, index) => customName ? customName : compressionResults.length === 1 ? targetAttachment?.name : compressedFiles[index].name);

        // Fire-and-forget — don't block the UI so the user can continue
        // working or start another upload while this one runs. Clear the
        // placeholder prep state once the upload mutation starts; from here
        // the optimistic upload tile handles progress/loading feedback.
        const upload = {
          files: compressedFiles,
          names,
          onProgress: (fileIndex: number, loaded: number, total: number) => {
            setUploadProgress(prev_1 => {
              const next_0 = new Map(prev_1);
              next_0.set(fileIndex, {
                loaded,
                total
              });
              return next_0;
            });
          }
        };
        if (studioMask && compressedFiles.length === 1) {
          // mutateAsync, not mutate's onSuccess: that one only fires for the
          // latest call, and the camera can take several shots in a row.
          void signedUrlUploadMutation.mutateAsync(upload).then(uploaded => {
            const photo = uploaded.length === 1 ? uploaded[0] : undefined;
            if (photo) void applyStudioToNewPhoto(photo, studioMask);
          }).catch(() => {
            // The mutation's own onError reports the failed upload.
          });
        } else {
          signedUrlUploadMutation.mutate(upload);
        }
        setPlaceholderPreparing(preparingPlaceholderId, false);
      } else {
        setPlaceholderPreparing(preparingPlaceholderId, false);
      }
    } catch {
      // handled in mutation
      setIsCompressing(false);
      setCompressionProgress(null);
      setUploadProgress(new Map());
      setPlaceholderPreparing(preparingPlaceholderId, false);
    }
  }, [patientId, signedUrlUploadMutation, setPlaceholderPreparing]);
  const {
    dragActive,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleClick,
    handleFileInputChange
  } = useFileDragDrop({
    onFilesSelected: handleFileChange,
    disabled: isProcessing || addDisabled
  });

  // Reset file input after compression completes so the same file can be re-selected
  useEffect(() => {
    if (!isCompressing && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [isCompressing, fileInputRef]);

  // Selection mode handlers
  const handleToggleSelection = useCallback((attachmentId_9: string) => {
    setSelectedAttachmentIds(prev_2 => {
      const next_1 = new Set(prev_2);
      if (next_1.has(attachmentId_9)) {
        next_1.delete(attachmentId_9);
      } else {
        next_1.add(attachmentId_9);
      }
      return next_1;
    });
  }, []);
  const handleDeselectAll = useCallback(() => {
    setSelectedAttachmentIds(new Set());
  }, []);
  const handleExitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedAttachmentIds(new Set());
  }, []);

  // Template placeholders aren't stored attachments, so tag, rename and
  // download only act on the real photos in the selection.
  const selectedRealAttachments = useMemo(() => attachments.filter(att_11 => selectedAttachmentIds.has(att_11.id) && !att_11.isPlaceholder), [attachments, selectedAttachmentIds]);
  const ensureRealPhotosSelected = useCallback((action: string) => {
    if (selectedRealAttachments.length > 0) return true;
    toast({
      title: `Can't ${action} template photos`,
      description: "Add a photo to the template slot first.",
      variant: "warning"
    });
    return false;
  }, [selectedRealAttachments.length]);
  const handleOpenBulkRename = useCallback(() => {
    if (ensureRealPhotosSelected("rename")) {
      setBulkRenameModalOpen(true);
    }
  }, [ensureRealPhotosSelected]);
  const handleOpenBulkDelete = useCallback(() => {
    if (selectedAttachmentIds.size > 0) {
      setBulkDeleteModalOpen(true);
    }
  }, [selectedAttachmentIds.size]);
  const handleOpenBulkTag = useCallback(() => {
    if (ensureRealPhotosSelected("tag")) {
      setBulkTagModalOpen(true);
    }
  }, [ensureRealPhotosSelected]);
  const handleConfirmBulkDelete = useCallback(async () => {
    if (selectedAttachmentIds.size === 0) return;

    // Template placeholders aren't stored attachments; dismiss them the way
    // single delete does and only send real photos to the API.
    const placeholderIds: string[] = [];
    const realIds: string[] = [];
    for (const att_12 of attachments) {
      if (!selectedAttachmentIds.has(att_12.id)) continue;
      if (att_12.isPlaceholder) placeholderIds.push(att_12.id);else realIds.push(att_12.id);
    }

    // Delete real photos first: if that fails, the template photos stay put.
    if (realIds.length > 0) {
      await bulkDeleteMutation.mutateAsync(realIds);
    }
    placeholderIds.forEach(id_0 => placeholderControls?.onPlaceholderDelete?.(id_0));
    const removedCount = realIds.length + placeholderIds.length;
    toast({
      title: "Success",
      description: `Deleted ${removedCount} photo${removedCount !== 1 ? "s" : ""}`,
      variant: "success"
    });
    handleExitSelectionMode();
    setBulkDeleteModalOpen(false);
  }, [attachments, bulkDeleteMutation, handleExitSelectionMode, placeholderControls, selectedAttachmentIds]);
  const handleBulkDownload = useCallback(async () => {
    if (!ensureRealPhotosSelected("download")) return;
    const selectedAttachments = selectedRealAttachments;
    setIsDownloading(true);
    let successCount = 0;
    let failCount = 0;
    for (let i_0 = 0; i_0 < selectedAttachments.length; i_0++) {
      const attachment_5 = selectedAttachments[i_0];
      const gcsPath = attachment_5.gcsPath;
      if (!gcsPath) {
        failCount++;
        continue;
      }
      try {
        const filename = attachment_5.name || `photo-${attachment_5.id.slice(0, 8)}.jpg`;
        const result = await getSignedDownloadUrl(gcsPath, filename);
        if (!result?.signedUrl) {
          failCount++;
          continue;
        }
        await downloadFromSignedUrl(result.signedUrl, filename);
        successCount++;

        // Small delay between downloads to avoid browser issues
        if (i_0 < selectedAttachments.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (error) {
        console.error(`Failed to download attachment ${attachment_5.id}:`, error);
        failCount++;
      }
    }
    setIsDownloading(false);
    if (successCount > 0 && failCount === 0) {
      toast({
        title: "Success",
        description: `Downloaded ${successCount} photo${successCount !== 1 ? "s" : ""}`,
        variant: "success"
      });
    } else if (successCount > 0 && failCount > 0) {
      toast({
        title: "Partial Success",
        description: `Downloaded ${successCount} photo${successCount !== 1 ? "s" : ""}, ${failCount} failed`,
        variant: "warning"
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to download photos",
        variant: "error"
      });
    }
    handleExitSelectionMode();
  }, [ensureRealPhotosSelected, selectedRealAttachments, handleExitSelectionMode]);

  // Delete handler
  const handleRemoveAttachment = useCallback((attachmentId_10: string) => {
    const attachment_6 = attachments.find(a_4 => a_4.id === attachmentId_10);
    if (attachment_6) {
      setAttachmentToDelete(attachment_6);
      setDeleteModalOpen(true);
    }
  }, [attachments]);

  // Unlink from note handler (remove from note, keep in gallery)
  const handleUnlinkFromNote = useCallback((attachmentId_11: string, targetNoteId_4?: string) => {
    const unlinkNoteId = targetNoteId_4 || noteId;
    if (!unlinkNoteId) return;
    unlinkMutation.mutate({
      attachmentId: attachmentId_11,
      targetNoteId: unlinkNoteId
    });
  }, [noteId, unlinkMutation]);

  // Convert attachment data to NoteAttachment format
  const toNoteAttachment = useCallback((attachment_7: AttachmentData): NoteAttachment => {
    const isPlaceholder = attachment_7.isPlaceholder === true;
    return {
      id: attachment_7.id,
      attachmentId: isPlaceholder ? undefined : attachment_7.attachmentId || attachment_7.id,
      url: attachment_7.signedUrl || attachment_7.url || "",
      gcsPath: attachment_7.gcsPath || "",
      annotations: attachment_7.annotations || [],
      name: attachment_7.name || "",
      isPlaceholder
    };
  }, []);
  const handleConfirmDelete = useCallback(async () => {
    if (!attachmentToDelete) return;
    const noteAttachment = toNoteAttachment(attachmentToDelete);

    // Handle placeholder deletion (for template attachments)
    if (placeholderControls && placeholderControls.isPlaceholder(noteAttachment)) {
      // Call the parent's onPlaceholderDelete callback if provided
      if (placeholderControls.onPlaceholderDelete) {
        placeholderControls.onPlaceholderDelete(attachmentToDelete.id);
      }
      toast({
        title: "Success",
        description: "Template attachment removed",
        variant: "success"
      });
      setDeleteModalOpen(false);
      setAttachmentToDelete(null);
      return;
    }
    if (attachmentToDelete.attachmentId || attachmentToDelete.id) {
      const attachmentId_12 = attachmentToDelete.attachmentId || attachmentToDelete.id;
      await deleteAttachmentMutation.mutateAsync(attachmentId_12);
      setDeleteModalOpen(false);
      setAttachmentToDelete(null);
    }
  }, [attachmentToDelete, deleteAttachmentMutation, placeholderControls, toNoteAttachment, noteId]);

  // Name editing handlers
  const handleEditName = useCallback((attachment_8: NoteAttachment) => {
    setEditingNameId(attachment_8.id);
    setEditingName(attachment_8.name || "");
  }, []);
  const handleSaveName = useCallback(async (attachmentId_13: string) => {
    const newName = editingName.trim();
    if (!newName) {
      handleCancelEditName();
      return;
    }
    const attachment_9 = attachments.find(a_5 => a_5.id === attachmentId_13);
    if (!attachment_9?.attachmentId && !attachment_9?.id) return;

    // Don't save if name hasn't changed
    if (newName === attachment_9.name) {
      setEditingNameId(null);
      setEditingName("");
      return;
    }
    setSavingNameId(attachmentId_13);
    try {
      await updateNameMutation.mutateAsync({
        attachmentId: attachment_9.attachmentId || attachment_9.id,
        name: newName
      });
      setEditingNameId(null);
      setEditingName("");
    } catch (error_0) {
      console.error("Failed to update attachment name:", error_0);
      // Error toast is handled by mutation
    } finally {
      setSavingNameId(null);
    }
  }, [editingName, attachments, updateNameMutation]);
  const handleCancelEditName = useCallback(() => {
    setEditingNameId(null);
    setEditingName("");
  }, []);

  // Duplicate handler
  const handleDuplicate = useCallback((attachmentId_14: string) => {
    duplicateAttachmentMutation.mutate(attachmentId_14);
  }, [duplicateAttachmentMutation]);

  // Gallery lightbox handler
  const handleOpenGallery = useCallback((index_0: number) => {
    setGalleryIndex(index_0);
    setGalleryOpen(true);
  }, []);

  // Link to note handler (for dropdown menu)
  const handleLinkToNote = useCallback((attachmentId_15: string) => {
    const att_13 = attachments.find(a_6 => a_6.id === attachmentId_15);
    if (!att_13) return;
    const resolved = resolveAttachment({
      attachment: {
        id: att_13.id,
        noteId: att_13.noteId || noteId || "",
        gcsPath: att_13.gcsPath || "",
        originalFilename: att_13.name,
        signedUrl: att_13.signedUrl || att_13.url,
        annotations: att_13.annotations || [],
        name: att_13.name,
        createdDate: att_13.date,
        note: att_13.note,
        patient: att_13.patient,
        tags: att_13.tags
      },
      shouldShowThumbnail: false,
      noteId,
      patientId
    });
    setNotePickerAttachment(resolved);
  }, [attachments, noteId, patientId]);

  // Change patient handler (for dropdown menu)
  const handleChangePatient = useCallback((attachmentId_16: string) => {
    const att_14 = attachments.find(a_7 => a_7.id === attachmentId_16);
    if (!att_14) return;
    const resolved_0 = resolveAttachment({
      attachment: {
        id: att_14.id,
        noteId: att_14.noteId || noteId || "",
        gcsPath: att_14.gcsPath || "",
        originalFilename: att_14.name,
        signedUrl: att_14.signedUrl || att_14.url,
        annotations: att_14.annotations || [],
        name: att_14.name,
        createdDate: att_14.date,
        note: att_14.note,
        patient: att_14.patient,
        tags: att_14.tags
      },
      shouldShowThumbnail: false,
      noteId,
      patientId
    });
    setChangePatientAttachment(resolved_0);
  }, [attachments, noteId, patientId]);

  // Resolve all attachments for the gallery dialog
  const resolvedAttachments = useMemo<ResolvedAttachment[]>(() => attachments.map(att_15 => resolveAttachment({
    attachment: {
      id: att_15.id,
      noteId: att_15.noteId || noteId || "",
      gcsPath: att_15.gcsPath || "",
      originalFilename: att_15.name,
      signedUrl: att_15.signedUrl || att_15.url,
      thumbnailSignedUrl: att_15.thumbnailSignedUrl,
      annotations: att_15.annotations || [],
      name: att_15.name,
      createdDate: att_15.date,
      note: att_15.note ? {
        id: att_15.note.id,
        createdDate: att_15.note.createdDate || att_15.date,
        visitDate: att_15.note.visitDate
      } : noteId ? {
        id: noteId,
        createdDate: att_15.date
      } : undefined,
      patient: att_15.patient,
      tags: att_15.tags
    },
    shouldShowThumbnail: false,
    noteId,
    patientId
  })), [attachments, noteId, patientId]);

  // Studio Mode in the lightbox: backdrop swatches and "Show as taken".
  const galleryCurrent = resolvedAttachments[galleryIndex];
  const studioViewer = useStudioViewer(galleryCurrent?.attachmentId, {
    open: galleryOpen,
    isImage: !!galleryCurrent && !galleryCurrent.isVideo && !galleryCurrent.isPlaceholder && isStudioImagePath(galleryCurrent.gcsPath)
  });

  // Gallery "Edit Annotations" handler
  const handleGalleryEditAnnotations = useCallback((resolved_1: ResolvedAttachment) => {
    const att_16 = attachments.find(a_8 => a_8.id === resolved_1.id);
    if (!att_16) return;
    const isPlaceholder_0 = att_16.isPlaceholder === true;
    const noteAttachment_0: NoteAttachment = {
      id: att_16.id,
      attachmentId: isPlaceholder_0 ? undefined : att_16.attachmentId || att_16.id,
      url: att_16.signedUrl || att_16.url || "",
      gcsPath: att_16.gcsPath || "",
      annotations: att_16.annotations || [],
      name: att_16.name || "",
      isPlaceholder: isPlaceholder_0,
      note: att_16.note
    };
    setAnnotatingAttachment(noteAttachment_0);
    setAnnotationModalOpen(true);
  }, [attachments]);

  // Compare handler
  const handleCompare = useCallback((attachment_10: AttachmentData) => {
    const response: NoteAttachment = {
      id: attachment_10.id,
      noteId: "",
      signedUrl: attachment_10.signedUrl || attachment_10.url,
      thumbnailSignedUrl: attachment_10.thumbnailSignedUrl,
      gcsPath: attachment_10.gcsPath || "",
      originalFilename: attachment_10.name,
      annotations: attachment_10.annotations || [],
      name: attachment_10.name,
      createdDate: attachment_10.date
    };
    setCompareSourceImage(response);
    setCompareModalOpen(true);
  }, []);

  // Annotation handlers — send `baseline` so the mutation can compute the diff
  // and send a single bulk-apply request.
  const handleSaveAnnotations = useCallback(async (annotations: any[], baseline: any[]) => {
    if (!annotatingAttachment) return;
    if (placeholderControls?.convertingPlaceholderId) {
      throw new Error("Template image is already being prepared");
    }
    let targetAttachment_0 = annotatingAttachment;
    let saveBaseline = baseline;
    if (placeholderControls?.isPlaceholder(targetAttachment_0)) {
      placeholderControls.setConvertingPlaceholderId(targetAttachment_0.id);
      try {
        const convertedAttachment = await placeholderControls.convertPlaceholderToRealAttachment(targetAttachment_0);
        if (!convertedAttachment) {
          // convertPlaceholderToRealAttachment already surfaced a specific toast
          // for the real failure modes (save the note first / missing question /
          // missing GCS path / creation failed). Don't overwrite it with a
          // generic "try saving again" message — retrying never fixes these.
          throw new Error("Failed to prepare template image for annotation");
        }
        targetAttachment_0 = convertedAttachment;
        // The baseline was the template image's marks, which the new copy
        // doesn't have yet; diff against what the copy actually holds.
        saveBaseline = convertedAttachment.annotations ?? [];
      } finally {
        placeholderControls.setConvertingPlaceholderId(null);
      }
    }
    const attachmentId_17 = targetAttachment_0.attachmentId || targetAttachment_0.id;
    if (!attachmentId_17 || attachmentId_17.startsWith("template-placeholder-")) {
      toast({
        title: "Save failed",
        description: "We couldn't prepare this template image. Your marks are still on screen—try saving again.",
        variant: "error"
      });
      throw new Error("Template image was not prepared for annotation save");
    }
    try {
      await updateAnnotationsMutation.mutateAsync({
        attachmentId: attachmentId_17,
        annotations,
        baseline: saveBaseline
      });
      setAnnotationModalOpen(false);
      setAnnotatingAttachment(null);
    } catch (error_1) {
      console.error("Failed to save annotations:", error_1);
      // Error toast is handled by mutation
      throw error_1;
    }
  }, [annotatingAttachment, placeholderControls, updateAnnotationsMutation]);

  // Memoize existing attachment IDs for gallery picker
  const existingAttachmentIds = useMemo(() => new Set(attachments.map(a_9 => a_9.id)), [attachments]);

  // Get selected attachments for bulk rename modal
  const selectedAttachmentsForRename = useMemo(() => {
    return selectedRealAttachments.map((att_17): NoteAttachment => ({
      id: att_17.id,
      noteId: "",
      // Not available in AttachmentData, but component doesn't seem to use it
      gcsPath: att_17.gcsPath || "",
      originalFilename: att_17.name,
      signedUrl: att_17.signedUrl || att_17.url,
      thumbnailSignedUrl: att_17.thumbnailSignedUrl,
      annotations: att_17.annotations || [],
      name: att_17.name,
      createdDate: att_17.date
    }));
  }, [selectedRealAttachments]);

  // Get selected attachments for bulk tag modal
  const selectedAttachmentsForTag = useMemo(() => {
    return selectedRealAttachments.map((att_18): NoteAttachment => ({
      id: att_18.id,
      noteId: "",
      gcsPath: att_18.gcsPath || "",
      originalFilename: att_18.name,
      signedUrl: att_18.signedUrl || att_18.url,
      thumbnailSignedUrl: att_18.thumbnailSignedUrl,
      annotations: att_18.annotations || [],
      name: att_18.name,
      createdDate: att_18.date,
      tags: att_18.tags
    }));
  }, [selectedRealAttachments]);

  // Memoize the onAssignTags handler
  const handleAssignTags = useCallback((attachmentId_18: string, tagIds: string[]) => {
    assignTagsMutation.mutate({
      attachmentId: attachmentId_18,
      tagIds
    });
  }, [assignTagsMutation]);

  // Camera modal is rendered standalone so placeholder tiles can also open it.
  const cameraModal = <CameraCaptureModal permissionDeniedToast={PHOTO_PERMISSION_DENIED_TOAST} open={cameraModalOpen} onOpenChange={open => {
    setCameraModalOpen(open);
    if (!open) setPlaceholderCameraTarget(null);
  }} onCapture={(image, extras) => {
    const file = image instanceof File ? image : new File([image], `photo-${Date.now()}.jpg`, {
      type: "image/jpeg"
    });
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    handleFileChange(dataTransfer.files, placeholderCameraTarget ?? undefined, undefined, extras?.studioMask);
  }} allowMultiple={!placeholderCameraTarget} allowVideo disabled={isCompressing || addDisabled} patientId={patientId} studioCapture />;
  const handleOpenPlaceholderCamera = useCallback((target_0: NoteAttachment) => {
    setPlaceholderCameraTarget(target_0);
    setCameraModalOpen(true);
  }, []);
  const canAddFromGallery = !uploadDisabled && !!patientId && !!noteId;
  const hasAddActions = showUpload || !!dropdownActions;
  return <div className={className}>
      {/* Upload buttons and Select button */}
      {(hasAddActions || canShowSelectionMode || allTags.length > 0 && onTagIdsChange) &&
    // Container query: the patient sidebar is narrow even on wide screens.
    <div className="@container mb-4 flex items-center justify-end gap-2">
          {allTags.length > 0 && onTagIdsChange && <MultiSelect value={selectedTagIds} onChange={handleTagIdsChange} options={allTags.map(tag_1 => ({
        value: tag_1.id,
        label: tag_1.name
      }))} className="shrink-0">
              <PopoverTrigger asChild>
                <button type="button" aria-label="Filter by tag" className={cx("relative flex h-9 items-center justify-center rounded-md border px-2.5 text-foreground shadow-sm outline-none transition md:hover:bg-row-hover", "@sm:w-48 @sm:justify-between @sm:px-3", focusInput)}>
                  <FiFilter className="h-4 w-4 @sm:hidden" />
                  {selectedTagIds.length > 0 && <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-caption-strong text-primary-foreground @sm:hidden">
                      {selectedTagIds.length}
                    </span>}
                  <span className="hidden truncate text-body @sm:inline">
                    {selectedTagNames || <span className="text-foreground-muted">
                        Filter by tag
                      </span>}
                  </span>
                  <RiArrowDownSLine className="hidden size-4 shrink-0 text-foreground-muted @sm:block" />
                </button>
              </PopoverTrigger>
              <MultiSelectContent>
                {allTags.map(tag_2 => <MultiSelectItem key={tag_2.id} value={tag_2.id}>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full border border-border" style={{
                backgroundColor: tag_2.color
              }} />
                      {tag_2.name}
                    </div>
                  </MultiSelectItem>)}
              </MultiSelectContent>
            </MultiSelect>}
          {isCompressing && <span className="flex items-center gap-1.5 text-caption text-foreground-secondary">
              <LuLoader2 className="h-3.5 w-3.5 animate-spin" />
              <span>
                {compressionProgress ? `Compressing ${compressionProgress.completed}/${compressionProgress.total}...` : "Compressing..."}
              </span>
            </span>}
          {isSelectionMode ? <Button variant="secondary" onClick={handleExitSelectionMode} className="px-2 py-1 text-caption">
              Done
            </Button> : <>
              {toolbarActions}
              {showUpload && <Button variant="secondary" onClick={() => setCameraModalOpen(true)} disabled={isCompressing || addDisabled} tooltip={addBlockedReason} aria-label="Capture" className="shrink-0 gap-1 px-2 py-1 text-caption">
                  <FiCamera className="h-4 w-4" />
                  <span className="hidden @sm:inline">Capture</span>
                </Button>}
              {(hasAddActions || canShowSelectionMode) && <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <DropdownSettingsButton variant="secondary" aria-label="Attachment actions" className="shrink-0" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {showUpload && <>
                        <DropdownMenuItem disabled={isCompressing || addDisabled} onClick={handleClick} icon={<FiUpload className="h-4 w-4" />} hint={addBlockedReason}>
                          Add files
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={!canAddFromGallery} onClick={() => setGalleryPickerOpen(true)} icon={<HiOutlinePhotograph className="h-4 w-4" />} hint={uploadDisabled || !patientId ? "Search for a patient first" : !noteId ? "Save the note first" : undefined}>
                          From patient gallery
                        </DropdownMenuItem>
                      </>}
                    {dropdownActions}
                    {canShowSelectionMode && <>
                        {hasAddActions && <DropdownMenuSeparator />}
                        <DropdownMenuItem disabled={!hasAny} onClick={() => setIsSelectionMode(true)} icon={<FiCheckSquare className="h-4 w-4" />}>
                          Select
                        </DropdownMenuItem>
                      </>}
                  </DropdownMenuContent>
                </DropdownMenu>}
            </>}
        </div>}

      {/* Loading State */}
      {isLoading && attachments.length === 0 && <div className="flex items-center justify-center rounded-lg border p-6">
          <div className="flex items-center gap-3">
            <LuLoader2 className="h-5 w-5 animate-spin text-foreground-muted" />
            <div className="text-body text-foreground-secondary">
              Loading attachments...
            </div>
          </div>
        </div>}

      {/* Empty State */}
      {!isLoading && attachments.length === 0 && <>
          {showUpload ? <Card onClick={handleClick} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={cx("cursor-pointer border-2 border-dashed p-6 text-center transition-colors", isProcessing ? "cursor-not-allowed opacity-50" : dragActive ? "border-primary bg-primary/5" : "border-border hover:border-border")}>
              {isProcessing ? <>
                  <LuLoader2 className="mx-auto mb-2 h-8 w-8 animate-spin text-foreground-muted" />
                  <p className="text-body-strong text-foreground-secondary">
                    {isCompressing ? compressionProgress ? `Compressing images (${compressionProgress.completed}/${compressionProgress.total})...` : "Compressing images..." : "Uploading files..."}
                  </p>
                  <p className="mt-1 text-caption text-muted-foreground">
                    Please wait while we process your files
                  </p>
                </> : <>
                  <HiOutlinePhotograph className="mx-auto mb-2 h-8 w-8 text-foreground-muted" />
                  <p className="text-body-strong text-foreground-secondary">
                    Upload medical files and add annotations
                  </p>
                  <p className="mt-1 text-caption text-muted-foreground">
                    Drop files here or click to upload
                  </p>
                  <p className="mt-1 text-caption text-foreground-muted">
                    Supports: JPG, PNG, HEIC, MP4, MOV and more
                  </p>
                </>}
            </Card> : <div className="flex flex-col items-center justify-center p-8 text-center">
              <HiOutlinePhotograph className="mb-3 h-12 w-12 text-foreground-muted" />
              <p className="text-body text-muted-foreground">No attachments</p>
            </div>}
        </>}

      {/* Attachments Grid */}
      {attachments.length > 0 && <div>
          {groupByDate && groupedAttachments ? groupedAttachments.map(group => <div key={group.dateKey} className="space-y-2">
                <div className="flex items-center justify-between gap-2 border-b pb-1">
                  <h4 className="text-body-strong text-foreground-secondary">
                    {formatDate(group.dateKey, {
              year: "numeric",
              month: "short",
              day: "numeric"
            })}
                  </h4>
                  {group.appointments.length === 1 && group.appointments[0].eventId && <GalleryAppointmentButton eventId={group.appointments[0].eventId} />}
                </div>
                {group.appointments.map(appointment => <div key={appointment.eventId ?? "unlinked"} className="space-y-2">
                    {group.appointments.length > 1 && <div className="flex items-center justify-between gap-2">
                        <h5 className="text-caption-strong text-foreground-secondary">
                          {appointment.eventId ? appointment.title || "Untitled appointment" : "No appointment"}
                        </h5>
                        {appointment.eventId && <GalleryAppointmentButton eventId={appointment.eventId} />}
                      </div>}
                    <div className={cx("grid gap-2", sizeClasses[size])}>
                      {appointment.data.map(attachment_11 => <AttachmentGridItem key={attachment_11.id} attachment={attachment_11} showCheckbox={true} noteId={noteId} patientId={patientId} allTags={allTags} isSelectionMode={isSelectionMode} isUploading={isUploading || preparingPlaceholderIds.has(attachment_11.id)} isAnyMutating={isAnyMutating} attachmentsCount={attachments.length} selectedAttachmentIds={selectedAttachmentIds} editingNameId={editingNameId} editingName={editingName} savingNameId={savingNameId} showPatientLink={showPatientLink} showNoteLink={showNoteLink && canViewNotes} placeholderControls={placeholderControls} onToggleSelection={handleToggleSelection} onRemoveAttachment={handleRemoveAttachment} onUnlinkFromNote={noteId ? handleUnlinkFromNote : undefined} onCompare={handleCompare} onDuplicate={handleDuplicate} isDuplicating={duplicateAttachmentMutation.isPending} onAssignTags={handleAssignTags} onEditName={handleEditName} onSaveName={handleSaveName} onCancelEditName={handleCancelEditName} setEditingName={setEditingName} setAnnotatingAttachment={setAnnotatingAttachment} setAnnotationModalOpen={setAnnotationModalOpen} openFullScreenAnnotationPage={openFullScreenAnnotationPage} handleFileChange={addDisabled ? undefined : handleFileChange} onCameraOpen={addDisabled ? undefined : handleOpenPlaceholderCamera} onOpenGallery={clickToAnnotate ? undefined : handleOpenGallery} attachmentIndex={attachmentIndexMap.get(attachment_11.id) ?? 0} onLinkToNote={handleLinkToNote} onChangePatient={handleChangePatient} onSkinAnalysis={hasSkinAnalysis ? setSkinAnalysisAttachmentId : undefined} onSetAsProfilePhoto={canEditProfilePicture ? handleSetAsProfilePhoto : undefined} onShareToPortal={enablePortalSharing ? handleShareToPortal : undefined} />)}
                    </div>
                  </div>)}
              </div>) : <div className={cx("grid gap-2", sizeClasses[size])}>
              {attachments.map((attachment_12, index_1) => <AttachmentGridItem key={attachment_12.id} attachment={attachment_12} showCheckbox={true} noteId={noteId} patientId={patientId} allTags={allTags} isSelectionMode={isSelectionMode} isUploading={isUploading || preparingPlaceholderIds.has(attachment_12.id)} isAnyMutating={isAnyMutating} attachmentsCount={attachments.length} selectedAttachmentIds={selectedAttachmentIds} editingNameId={editingNameId} editingName={editingName} savingNameId={savingNameId} showPatientLink={showPatientLink} showNoteLink={showNoteLink && canViewNotes} placeholderControls={placeholderControls} onToggleSelection={handleToggleSelection} onRemoveAttachment={handleRemoveAttachment} onUnlinkFromNote={noteId ? handleUnlinkFromNote : undefined} onCompare={handleCompare} onDuplicate={handleDuplicate} isDuplicating={duplicateAttachmentMutation.isPending} onAssignTags={handleAssignTags} onEditName={handleEditName} onSaveName={handleSaveName} onCancelEditName={handleCancelEditName} setEditingName={setEditingName} setAnnotatingAttachment={setAnnotatingAttachment} setAnnotationModalOpen={setAnnotationModalOpen} openFullScreenAnnotationPage={openFullScreenAnnotationPage} handleFileChange={addDisabled ? undefined : handleFileChange} onCameraOpen={addDisabled ? undefined : handleOpenPlaceholderCamera} onOpenGallery={clickToAnnotate ? undefined : handleOpenGallery} attachmentIndex={index_1} onLinkToNote={handleLinkToNote} onChangePatient={handleChangePatient} onSkinAnalysis={hasSkinAnalysis ? setSkinAnalysisAttachmentId : undefined} onSetAsProfilePhoto={canEditProfilePicture ? handleSetAsProfilePhoto : undefined} onShareToPortal={enablePortalSharing ? handleShareToPortal : undefined} />)}
            </div>}
          {hasNextPage && loadMoreRef && <div ref={loadMoreRef} className="py-2 text-center text-caption text-foreground-muted">
              {isLoading ? "Loading more..." : "Scroll to load more"}
            </div>}
        </div>}

      {cameraModal}

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif,video/*" multiple className="hidden" onChange={handleFileInputChange} disabled={addDisabled} />

      {/* Gallery Lightbox */}
      <AttachmentGalleryDialog open={galleryOpen} onOpenChange={setGalleryOpen} attachments={resolvedAttachments} imageUrlOverride={studioViewer.imageUrl} imageTransparent={studioViewer.isTransparent} headerExtras={studioViewer.controls} onShowAsTaken={studioViewer.showAsTaken ?? undefined} currentIndex={galleryIndex} onIndexChange={setGalleryIndex} onEditAnnotations={handleGalleryEditAnnotations} onLinkToNote={canViewNotes ? attachment_13 => setNotePickerAttachment(attachment_13) : undefined} onChangePatient={attachment_14 => setChangePatientAttachment(attachment_14)} onDuplicate={attachment_15 => handleDuplicate(attachment_15.id)} onRename={attachment_16 => {
      const att_19 = attachments.find(a_10 => a_10.id === attachment_16.id);
      if (att_19) handleEditName(toNoteAttachment(att_19));
      setGalleryOpen(false);
    }} onCompare={attachment_17 => {
      const att_20 = attachments.find(a_11 => a_11.id === attachment_17.id);
      if (att_20) handleCompare(att_20);
      setGalleryOpen(false);
    }} onRemove={attachment_18 => {
      handleRemoveAttachment(attachment_18.id);
      setGalleryOpen(false);
    }} onUnlinkFromNote={noteId ? attachment_19 => {
      handleUnlinkFromNote(attachment_19.id);
      setGalleryOpen(false);
    } : undefined} onSkinAnalysis={hasSkinAnalysis ? attachment_20 => {
      setSkinAnalysisAttachmentId(attachment_20.id);
    } : undefined} onSetAsProfilePhoto={canEditProfilePicture ? attachment_21 => {
      const att_21 = attachments.find(a_12 => a_12.id === attachment_21.id);
      if (att_21) handleSetAsProfilePhoto(att_21);
    } : undefined} isDuplicating={duplicateAttachmentMutation.isPending} isAnyMutating={isAnyMutating} />

      {/* Modals */}
      <AttachmentAnnotationModal open={annotationModalOpen} onOpenChange={setAnnotationModalOpen} attachment={annotatingAttachment} patientId={patientId || undefined} appointmentId={appointmentId ?? annotatingAttachment?.note?.eventId} initialAnnotations={annotatingAttachment?.annotations || []} onSave={handleSaveAnnotations} onImageReplaced={onImageReplaced} isSaving={isAnyMutating || false} imageFile={annotatingAttachment?.file} annotationDraftScope={annotationDraftScope} extraActions={annotatingAttachment ? ({
      hasUnsavedChanges
    }) => {
      const isPlaceholder_1 = placeholderControls?.isPlaceholder(annotatingAttachment) === true;
      return <div className="relative">
                    <Button variant="secondary" disabled={isPlaceholder_1} icon={<FaExpand />} size="icon" tooltip={isPlaceholder_1 ? "Save this template image before opening full screen" : hasUnsavedChanges ? "Open in full screen with unsaved annotations" : "Open in full screen"} onClick={() => void openAnnotatingAttachmentFullScreen()} />
                  </div>;
    } : undefined} onRestore={annotatingAttachment ? async (historyId: string) => {
      const attachmentId_19 = annotatingAttachment.attachmentId || annotatingAttachment.id;
      if (attachmentId_19) {
        await restoreAnnotationMutation.mutateAsync({
          attachmentId: attachmentId_19,
          historyId
        });
      }
    } : undefined} isRestoring={restoreAnnotationMutation.isPending} />

      <DeleteConfirmationModal isOpen={deleteModalOpen} setIsOpen={open_0 => {
      setDeleteModalOpen(open_0);
      if (!open_0) {
        setAttachmentToDelete(null);
      }
    }} onConfirm={handleConfirmDelete} title="Delete Attachment" subtitle="Are you sure you want to delete this attachment? This action cannot be undone and will permanently remove the file and all its annotations." confirmText="Delete Attachment" />

      <DeleteConfirmationModal isOpen={bulkDeleteModalOpen} setIsOpen={setBulkDeleteModalOpen} onConfirm={handleConfirmBulkDelete} title="Delete Photos" subtitle={`Are you sure you want to delete ${selectedAttachmentIds.size} photo${selectedAttachmentIds.size !== 1 ? "s" : ""}? This action cannot be undone.`} confirmText="Delete Photos" />

      <ShareToPortalModal open={!!shareToPortalAttachment} onOpenChange={open_1 => {
      if (!open_1) setShareToPortalAttachment(null);
    }} attachment={shareToPortalAttachment ? {
      id: shareToPortalAttachment.id,
      name: shareToPortalAttachment.name,
      thumbnailUrl: shareToPortalAttachment.thumbnailSignedUrl || shareToPortalAttachment.signedUrl || shareToPortalAttachment.url,
      visibleToPatient: shareToPortalAttachment.visibleToPatient,
      patientCaption: shareToPortalAttachment.patientCaption
    } : null} />

      <BulkRenameAttachmentsModal open={bulkRenameModalOpen} onOpenChange={open_2 => {
      setBulkRenameModalOpen(open_2);
      if (!open_2) {
        handleExitSelectionMode();
      }
    }} attachments={selectedAttachmentsForRename} patientId={patientId} noteId={noteId} />

      <BulkTagAttachmentsModal open={bulkTagModalOpen} onOpenChange={open_3 => {
      setBulkTagModalOpen(open_3);
      if (!open_3) {
        handleExitSelectionMode();
      }
    }} attachments={selectedAttachmentsForTag} onComplete={handleExitSelectionMode} />

      <ImageCompareModal open={compareModalOpen} onOpenChange={open_4 => {
      setCompareModalOpen(open_4);
      if (!open_4) {
        setCompareSourceImage(null);
      }
    }} sourceImage={compareSourceImage} hasMoreImages={hasNextPage} isLoadingMoreImages={isFetchingNextPage} onLoadMoreImages={fetchNextPage} availableImages={attachments.map(a_13 => ({
      id: a_13.id,
      attachmentId: a_13.attachmentId || a_13.id,
      noteId: a_13.noteId || "",
      signedUrl: a_13.signedUrl || a_13.url,
      thumbnailSignedUrl: a_13.thumbnailSignedUrl,
      gcsPath: a_13.gcsPath || "",
      originalFilename: a_13.name,
      annotations: a_13.annotations || [],
      name: a_13.name,
      createdDate: a_13.date,
      patient: a_13.patient
    }))} patientId={patientId} />

      {patientId && noteId && <GalleryPickerModal open={galleryPickerOpen} onOpenChange={setGalleryPickerOpen} patientId={patientId} noteId={noteId} existingAttachmentIds={existingAttachmentIds} />}

      {/* Note Picker Modal */}
      {notePickerAttachment && notePickerAttachment.patientId && <NotePickerModal open={!!notePickerAttachment} onOpenChange={open_5 => {
      if (!open_5) setNotePickerAttachment(null);
    }} patientId={notePickerAttachment.patientId} currentNoteId={notePickerAttachment.noteId} linkedNoteIds={notePickerAttachment.noteId ? [notePickerAttachment.noteId] : []} onSave={noteIds => {
      const initialNoteId = notePickerAttachment.noteId;
      const selectedSet = new Set(noteIds);

      // Link newly selected notes
      for (const id_1 of noteIds) {
        if (id_1 !== initialNoteId) {
          linkToNoteMutation.mutate({
            noteId: id_1,
            attachmentId: notePickerAttachment.id
          });
        }
      }

      // Unlink deselected notes
      if (initialNoteId && !selectedSet.has(initialNoteId)) {
        handleUnlinkFromNote(notePickerAttachment.id, initialNoteId);
      }
      setNotePickerAttachment(null);
    }} isSaving={linkToNoteMutation.isPending || unlinkMutation.isPending} />}

      {/* Change Patient Modal */}
      {changePatientAttachment && <ChangePatientModal open={!!changePatientAttachment} onOpenChange={open_6 => {
      if (!open_6) setChangePatientAttachment(null);
    }} currentPatientName={changePatientAttachment.patient?.firstName ? formatName(changePatientAttachment.patient.firstName, changePatientAttachment.patient.lastName) : undefined} onConfirm={patient => {
      changePatientMutation.mutate({
        attachmentId: changePatientAttachment.id,
        newPatientId: patient.id
      }, {
        onSuccess: () => {
          setChangePatientAttachment(null);
          setGalleryOpen(false);
        }
      });
    }} isChanging={changePatientMutation.isPending} />}

      {/* Skin Analysis Dialog */}
      <SkinAnalysisDialog open={!!skinAnalysisAttachmentId} onOpenChange={open_7 => {
      if (!open_7) setSkinAnalysisAttachmentId(null);
    }} attachmentId={skinAnalysisAttachmentId} />

      {/* Bulk Actions Bar - Sticky at bottom */}
      {isSelectionMode && selectedAttachmentIds.size > 0 && <BulkActionsBar selectedCount={selectedAttachmentIds.size} onClear={handleDeselectAll} isLoading={isDownloading} actions={[...(canDownload ? [{
      id: "download",
      label: isDownloading ? "Downloading..." : "Download",
      onClick: handleBulkDownload,
      icon: <FiDownload className="h-4 w-4" />,
      disabled: isDownloading
    }] : []), ...(photoActions.canEdit ? [{
      id: "tag",
      label: "Tag",
      onClick: handleOpenBulkTag,
      icon: null
    }] : []), ...(photoActions.canRename ? [{
      id: "rename",
      label: "Rename",
      onClick: handleOpenBulkRename,
      icon: null
    }] : []), ...(photoActions.canEdit ? [{
      id: "delete",
      label: "Delete",
      onClick: handleOpenBulkDelete,
      icon: null
    }] : [])]} position="sticky" />}
    </div>;
}